import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import EventsCalendar from '../components/EventsCalendar';
import { eventsAPI, postsAPI } from '../api';
import { Calendar, MapPin, ArrowRight, Sparkles, Clock, CalendarPlus } from 'lucide-react';

const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
const fmtTime = (iso) =>
  new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

const EventsPage = () => {
  // The brief requires the title's month and year to follow the real calendar.
  const MONTH_YEAR = new Date().toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
  const [upcoming, setUpcoming] = useState([]);
  const [legacyPosts, setLegacyPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [exhausted, setExhausted] = useState(false);
  // 95% of the feed is Glasgow venues, so a Paisley site should lead with
  // Paisley. Readers can widen it, and the choice sticks.
  const [localOnly, setLocalOnly] = useState(() => {
    try {
      return localStorage.getItem('pa_events_all_areas') !== '1';
    } catch {
      return true;
    }
  });
  const [totals, setTotals] = useState({ local: null, all: null });

  const PAGE = 24;

  useEffect(() => {
    setLoading(true);
    Promise.all([
      eventsAPI.list({ upcoming_only: true, limit: PAGE, local_only: localOnly }).catch(() => ({ data: [] })),
      // Drop has_image filter so the archive shows even when posts lack images
      postsAPI.getAll('events', 12, false).catch(() => ({ data: [] })),
      eventsAPI.count({ upcoming_only: true, local_only: true }).catch(() => ({ data: {} })),
      eventsAPI.count({ upcoming_only: true }).catch(() => ({ data: {} })),
    ]).then(([up, legacy, localCount, allCount]) => {
      const rows = up.data || [];
      setUpcoming(rows);
      setExhausted(rows.length < PAGE);
      setLegacyPosts((legacy.data || []).filter((p) => p.published));
      setTotals({ local: localCount.data?.count ?? null, all: allCount.data?.count ?? null });
      setLoading(false);
    });
  }, [localOnly]);

  const toggleArea = () => {
    const next = !localOnly;
    setLocalOnly(next);
    try {
      localStorage.setItem('pa_events_all_areas', next ? '0' : '1');
    } catch { /* choice just will not persist */ }
  };

  // Paisley runs hundreds of events a month, so anything more than a week or
  // two away sat past the first page with no way to reach it.
  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const res = await eventsAPI
        .list({ upcoming_only: true, limit: PAGE, skip: upcoming.length, local_only: localOnly })
        .catch(() => ({ data: [] }));
      const rows = res.data || [];
      setUpcoming((prev) => [...prev, ...rows]);
      if (rows.length < PAGE) setExhausted(true);
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={`What's On in Paisley This Week & Weekend | ${MONTH_YEAR}`}
        description="Find concerts, festivals, family activities and community events happening in Paisley and Renfrewshire this week, this weekend and beyond."
        keywords="Paisley events, what's on Paisley, Paisley calendar, Renfrewshire events, Paisley music, Paisley festival"
        url="/events"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: `What's On in Paisley This Week & Weekend | ${MONTH_YEAR}`,
            description: "Find concerts, festivals, family activities and community events happening in Paisley and Renfrewshire this week, this weekend and beyond.",
            url: "https://paisley.org.uk/events",
            isPartOf: { "@type": "WebSite", name: "Paisley.org.uk", url: "https://paisley.org.uk" },
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://paisley.org.uk" },
              { "@type": "ListItem", position: 2, name: "Events", item: "https://paisley.org.uk/events" },
            ],
          },
          // ItemList only — full Event markup belongs on each event's own page,
          // and we never invent a venue or date that isn't stored.
          ...(upcoming.length ? [{
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: "Upcoming events in Paisley and Renfrewshire",
            numberOfItems: upcoming.length,
            itemListElement: upcoming.map((ev, i) => ({
              "@type": "ListItem",
              position: i + 1,
              url: `https://paisley.org.uk/events/${ev.slug}`,
              name: ev.title,
            })),
          }] : []),
        ]}
      />
      <Header />

      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-cyan-950 py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-10 left-10 w-72 h-72 bg-cyan-500/20 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-72 h-72 bg-fuchsia-500/10 rounded-full blur-3xl" />
        </div>
        <div className="max-w-7xl mx-auto px-6 text-center relative">
          <p className="text-cyan-400 text-sm font-semibold uppercase tracking-[0.2em] mb-3">What's on in Paisley</p>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4 tracking-tight" data-testid="events-page-h1">
            Paisley Events
          </h1>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto">
            From Sma' Shot Day to the Halloween Festival — discover everything happening in and around Paisley.
          </p>
          <div className="mt-6">
            <Link
              to="/events/submit"
              className="inline-flex items-center gap-2 bg-white text-slate-900 px-5 py-2.5 rounded-full font-semibold text-sm hover:bg-cyan-100 transition"
              data-testid="events-page-submit-cta"
            >
              <CalendarPlus size={16} /> Running an event? List it here
            </Link>
          </div>
        </div>
      </section>

      {/* Calendar + Upcoming list */}
      <section className="py-16 -mt-12 relative">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-[440px_1fr] gap-8">
            {/* Sticky calendar */}
            <div className="lg:sticky lg:top-6 lg:self-start">
              <EventsCalendar localOnly={localOnly} />
            </div>

            {/* Upcoming list */}
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                  <Sparkles size={20} className="text-cyan-500" /> Upcoming Events
                </h2>
                <span className="text-sm text-gray-500">{upcoming.length} event{upcoming.length === 1 ? '' : 's'}</span>
              </div>

              <div className="flex flex-wrap items-center gap-3 mb-6 p-3 rounded-xl bg-slate-50 border border-gray-200">
                <span className="text-sm text-slate-700">
                  {localOnly ? 'Showing Paisley & Renfrewshire only' : 'Showing Paisley, Renfrewshire & Glasgow'}
                  {totals.local != null && totals.all != null && (
                    <span className="text-gray-500">
                      {' '}· {localOnly ? totals.local : totals.all} events
                    </span>
                  )}
                </span>
                <button
                  type="button"
                  onClick={toggleArea}
                  className="ml-auto text-sm font-semibold px-3 py-1.5 rounded-lg bg-white border border-gray-300 text-slate-800 hover:border-cyan-500 hover:text-cyan-700 transition-colors"
                  data-testid="events-area-toggle"
                >
                  {localOnly
                    ? `Include Glasgow${totals.local != null && totals.all != null ? ` (${totals.all - totals.local} more)` : ''}`
                    : 'Paisley & Renfrewshire only'}
                </button>
              </div>
              {loading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-28 bg-slate-100 rounded-xl animate-pulse" />
                  ))}
                </div>
              ) : upcoming.length === 0 ? (
                <div className="text-center py-16 bg-slate-50 rounded-2xl border border-dashed border-gray-200">
                  <Calendar size={32} className="mx-auto text-gray-400 mb-3" />
                  <p className="text-gray-600">No upcoming events yet — check back soon.</p>
                </div>
              ) : (
                <div className="space-y-4" data-testid="upcoming-events-list">
                  {upcoming.map((ev) => (
                    <Link
                      key={ev.id}
                      to={`/events/${ev.slug}`}
                      className="group flex gap-4 p-4 bg-white border border-gray-200 rounded-xl hover:border-cyan-500 hover:shadow-lg transition-all"
                      data-testid={`upcoming-event-card-${ev.slug}`}
                    >
                      {/* Date block */}
                      <div className="shrink-0 w-20 text-center bg-gradient-to-b from-slate-900 to-slate-800 text-white rounded-lg py-3 px-2 self-start">
                        <p className="text-[10px] uppercase tracking-wider text-cyan-400 font-semibold">
                          {new Date(ev.start_date).toLocaleString('en-GB', { month: 'short' })}
                        </p>
                        <p className="text-2xl font-bold leading-tight">
                          {new Date(ev.start_date).getDate()}
                        </p>
                        <p className="text-[10px] text-gray-400">
                          {new Date(ev.start_date).toLocaleString('en-GB', { weekday: 'short' })}
                        </p>
                      </div>
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        {(ev.featured || ev.sold_out) && (
                          <div className="flex flex-wrap items-center gap-2 mb-1">
                            {ev.featured && (
                              <span className="inline-block text-[10px] uppercase font-semibold tracking-wide text-fuchsia-600 bg-fuchsia-50 px-2 py-0.5 rounded">★ Featured</span>
                            )}
                            {ev.sold_out && (
                              <span
                                className="inline-block text-[10px] uppercase font-bold tracking-wide text-rose-700 bg-rose-50 border border-rose-200 px-2 py-0.5 rounded"
                                data-testid={`event-sold-out-${ev.slug}`}
                              >
                                Sold out
                              </span>
                            )}
                          </div>
                        )}
                        <h3 className="text-lg font-semibold text-slate-900 group-hover:text-cyan-600 transition-colors line-clamp-1">
                          {ev.title}
                        </h3>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 mt-1">
                          <span className="flex items-center gap-1"><Clock size={12} />{fmtTime(ev.start_date)}</span>
                          {ev.venue && <span className="flex items-center gap-1"><MapPin size={12} />{ev.venue}</span>}
                          <span className="capitalize bg-slate-100 px-2 rounded">{ev.category}</span>
                        </div>
                        {ev.description && (
                          <p className="text-sm text-gray-600 mt-2 line-clamp-2">{ev.description}</p>
                        )}
                      </div>
                      <ArrowRight size={18} className="text-gray-300 group-hover:text-cyan-500 group-hover:translate-x-1 transition-all self-center" />
                    </Link>
                  ))}
                  {!exhausted && (
                    <button
                      type="button"
                      onClick={loadMore}
                      disabled={loadingMore}
                      className="w-full py-3.5 rounded-xl border border-gray-200 text-sm font-semibold text-slate-700 hover:border-cyan-500 hover:text-cyan-700 disabled:opacity-60 transition-colors"
                      data-testid="events-load-more"
                    >
                      {loadingMore ? 'Loading...' : 'Show more events'}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Legacy events from the posts archive — always shown so admins always
          have a place to surface event blog content even before live events
          have been added. */}
      <section className="py-16 bg-slate-50 border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-2xl font-bold text-slate-900 mb-2">From the Events Blog</h2>
          <p className="text-gray-600 mb-8">Past coverage and event news from our archive of 4,500+ event posts.</p>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5" data-testid="events-archive-grid">
            {legacyPosts.length === 0 ? (
              <p className="col-span-full text-sm text-gray-500 italic">No event blog posts yet.</p>
            ) : (
              legacyPosts.map((p) => (
                <Link key={p.id} to={`/news/${p.slug}`} className="group">
                  <div className="bg-white rounded-xl overflow-hidden border border-gray-200 hover:border-cyan-500 hover:shadow-lg transition-all h-full">
                    <div className="relative h-40 overflow-hidden">
                      <img src={p.featured_image} alt={p.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                           onError={(e) => { e.target.src = '/event-placeholder.jpg'; }} />
                    </div>
                    <div className="p-4">
                      <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-2">
                        <Calendar size={12} />
                        <span>{new Date(p.created_at).toLocaleDateString()}</span>
                      </div>
                      <h3 className="text-sm font-bold text-slate-900 group-hover:text-cyan-500 line-clamp-2">{p.title}</h3>
                      <p className="text-gray-600 text-xs mt-2 line-clamp-2">{p.excerpt}</p>
                    </div>
                  </div>
                </Link>
              ))
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default EventsPage;
